import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { IconBadge, STAT_ICONS } from '../common/designSystem';
import FeaturePickerDialog from './FeaturePickerDialog';

/**
 * Fourth card on a feature plane: opens that plane's full feature list and
 * navigates to whichever one is picked.
 *
 * Shaped like a StatCard so the row reads as four equal cards, but it behaves
 * as a button that opens the searchable picker — a plain dropdown stopped
 * being usable once the Shoorah plane grew past thirty features.
 *
 * The card counts only what isn't already on the dashboard (`count`), while
 * the dialog lists everything on the plane, so searching for a headline
 * feature like Sleep still finds it.
 */
const SelectMoreCard = ({ label, features, count, onSelect, title, note, groupOrder }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full h-full text-left rounded-input border-[1.5px] border-dashed border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder py-3 px-[19px] transition-colors hover:border-shoorah-active focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active"
      >
        <div className="flex items-center gap-4">
          <IconBadge src={STAT_ICONS.more} />
          <div className="min-w-0">
            <p className="text-[13px] text-gray-500 dark:text-shoorah-darkBg truncate">{label}</p>
            <p className="mt-0.5 text-[16px] leading-tight font-semibold text-shoorah-btn dark:text-white truncate">
              {count ?? features.length} more
            </p>
          </div>
        </div>
      </button>

      <FeaturePickerDialog
        open={open}
        onClose={() => setOpen(false)}
        features={features}
        onSelect={onSelect}
        title={title || label}
        note={note}
        groupOrder={groupOrder}
      />
    </>
  );
};

SelectMoreCard.propTypes = {
  label: PropTypes.string,
  features: PropTypes.array.isRequired,
  count: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  title: PropTypes.string,
  note: PropTypes.string,
  groupOrder: PropTypes.arrayOf(PropTypes.string)
};

SelectMoreCard.defaultProps = {
  label: 'Select more',
  count: null,
  title: null,
  note: null,
  groupOrder: undefined
};

export default SelectMoreCard;
